import { useEffect, useState } from 'react';
import { contentApi } from '../../services/api';

interface FooterConfig {
  slogan: { es: string; en: string };
  show_weather: boolean;
  social: {
    active: boolean;
    facebook?: string;
    twitter?: string;
    instagram?: string;
  };
}

const DEFAULT: FooterConfig = {
  slogan: { es: '', en: '' },
  show_weather: true,
  social: { active: true },
};

export default function FooterConfigEditor() {
  const [config, setConfig] = useState<FooterConfig>(DEFAULT);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const res = await contentApi.getFooterConfig();
      if (res.data?.data) setConfig(res.data.data);
    } catch (e) {
      console.error('Failed to load footer config', e);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await contentApi.saveFooterConfig(config);
      setDirty(false);
    } catch (e) {
      console.error('Failed to save footer config', e);
    } finally {
      setSaving(false);
    }
  };

  const update = (path: string, value: any) => {
    setConfig(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      const keys = path.split('.');
      let obj = next;
      for (let i = 0; i < keys.length - 1; i++) obj = obj[keys[i]];
      obj[keys[keys.length - 1]] = value;
      return next;
    });
    setDirty(true);
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-400">Cargando...</div>;

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white">Configurar Footer</h2>
        <button
          onClick={handleSave}
          disabled={!dirty || saving}
          className={`px-4 py-2 rounded text-sm font-medium ${
            dirty ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          }`}
        >
          {saving ? 'Guardando...' : 'Guardar cambios'}
        </button>
      </div>

      <div className="space-y-6">
        {/* Slogan */}
        <div className="border rounded-lg dark:border-gray-700 p-4">
          <h3 className="font-medium text-gray-900 dark:text-white mb-3">Slogan</h3>
          <div className="space-y-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Espanol</label>
              <input
                type="text"
                value={config.slogan.es}
                onChange={(e) => update('slogan.es', e.target.value)}
                className="w-full px-3 py-2 text-sm border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">English</label>
              <input
                type="text"
                value={config.slogan.en}
                onChange={(e) => update('slogan.en', e.target.value)}
                className="w-full px-3 py-2 text-sm border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
            </div>
          </div>
        </div>

        {/* Weather */}
        <div className="border rounded-lg dark:border-gray-700 p-4">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={config.show_weather}
              onChange={(e) => update('show_weather', e.target.checked)}
              className="w-5 h-5 rounded"
            />
            <div>
              <span className="font-medium text-gray-900 dark:text-white">Widget del Tiempo</span>
              <p className="text-xs text-gray-500">Muestra el clima actual en el footer</p>
            </div>
          </label>
        </div>

        {/* Social */}
        <div className="border rounded-lg dark:border-gray-700 p-4">
          <label className="flex items-center gap-3 cursor-pointer mb-4">
            <input
              type="checkbox"
              checked={config.social.active}
              onChange={(e) => update('social.active', e.target.checked)}
              className="w-5 h-5 rounded"
            />
            <div>
              <span className="font-medium text-gray-900 dark:text-white">Redes Sociales</span>
              <p className="text-xs text-gray-500">Muestra iconos de redes sociales en el footer</p>
            </div>
          </label>

          {config.social.active && (
            <div className="space-y-3 pl-8">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Facebook URL</label>
                <input
                  type="text"
                  value={config.social.facebook || ''}
                  onChange={(e) => update('social.facebook', e.target.value || undefined)}
                  className="w-full px-3 py-2 text-sm border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  placeholder="https://facebook.com/..."
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Twitter / X URL</label>
                <input
                  type="text"
                  value={config.social.twitter || ''}
                  onChange={(e) => update('social.twitter', e.target.value || undefined)}
                  className="w-full px-3 py-2 text-sm border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  placeholder="https://x.com/..."
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Instagram URL</label>
                <input
                  type="text"
                  value={config.social.instagram || ''}
                  onChange={(e) => update('social.instagram', e.target.value || undefined)}
                  className="w-full px-3 py-2 text-sm border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  placeholder="https://instagram.com/..."
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {!dirty && (
        <p className="mt-4 text-sm text-gray-400 text-center">
          Los cambios se reflejan en la web de la organizacion al guardar.
        </p>
      )}
    </div>
  );
}
